import { handleSendMessage } from '@/helpers';
import { tokenSelector } from '@/redux/features/auth.slice';
import commonSlice from '@/redux/features/common.slice';
import { RootState } from '@/redux/store';
import { BlockStack, Button, InlineStack, Text } from '@shopify/polaris';
import { CalendarIcon, ChatIcon, XIcon } from '@shopify/polaris-icons';
import { memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as S from './styled';

export interface HelpItem {
  id: string;
  label: string;
  showBullet?: boolean;
}

interface HelpBannerProps {
  id: string;
  title: string;
  helpItems: HelpItem[];
  // TR-480 (optional, backward-compatible): khi truyền vào (chỉ Plan page) và shop eligible,
  // hiển thị thêm nút "1:1 Strategy Call" cạnh "Chat with us". onStrategyCall mở StrategyCallModal.
  isStrategyCallEligible?: boolean;
  onStrategyCall?: () => void;
}

const HelpBanner = ({ id, title, helpItems, isStrategyCallEligible = false, onStrategyCall }: HelpBannerProps) => {
  const { shop } = useSelector(tokenSelector);
  id = `${shop}:${id}`;
  const dispatch = useDispatch();
  const { dismissIdsCount, dismissIds } = useSelector((state: RootState) => state.common);

  const handleClose = () => {
    dispatch(commonSlice.actions.setDismissId(id));
    dispatch(commonSlice.actions.increaseDismissIdCount(id));
  };

  const handleChatClick = () => {
    handleSendMessage('Hi, I need support');
  };

  // TR-480: banner Plan ("Need custom solution?") truyền onStrategyCall → áp redesign Figma 4987-16144
  // (nút Strategy Call default + icon lịch, "Chat with us" plain, description 1 khối). 2 page kia giữ nguyên.
  const isPlanBanner = !!onStrategyCall;

  const midPoint = Math.ceil(helpItems.length / 2);
  const firstColumnItems = helpItems.slice(0, midPoint);
  const secondColumnItems = helpItems.slice(midPoint);

  if (dismissIds.includes(id) || (dismissIdsCount[id] && dismissIdsCount[id] >= 3)) return null;

  return (
    <S.BannerContainer>
      <S.BannerContent>
        <S.Content>
          <InlineStack gap="800" blockAlign="center" wrap={false}>
            <S.IconWrapper>
              <CustomIcon />
            </S.IconWrapper>
            <BlockStack gap="200">
              <Text as="p" variant="headingXl" fontWeight="semibold">
                {title}
              </Text>
              <InlineStack gap="400" blockAlign="center">
                {isStrategyCallEligible && onStrategyCall && (
                  <Button icon={CalendarIcon} onClick={onStrategyCall}>
                    1:1 Strategy Call
                  </Button>
                )}
                <Button icon={ChatIcon} variant={isPlanBanner ? 'plain' : undefined} onClick={handleChatClick}>
                  Chat with us
                </Button>
              </InlineStack>
            </BlockStack>
          </InlineStack>

          {isPlanBanner
            ? helpItems.length > 0 && (
                <BlockStack gap="200">
                  {helpItems.map((item) => (
                    <S.ListItem key={item.id}>
                      {item.showBullet !== false && <S.Bullet>•</S.Bullet>}
                      <Text as="span" variant="bodyMd">
                        {item.label}
                      </Text>
                    </S.ListItem>
                  ))}
                </BlockStack>
              )
            : (
              <>
                {firstColumnItems.length > 0 && (
                  <BlockStack gap="200">
                    {firstColumnItems.map((item) => (
                      <S.ListItem key={item.id}>
                        {item.showBullet !== false && <S.Bullet>•</S.Bullet>}
                        <Text as="span" variant="bodyMd">
                          {item.label}
                        </Text>
                      </S.ListItem>
                    ))}
                  </BlockStack>
                )}

                {secondColumnItems.length > 0 && (
                  <BlockStack gap="200">
                    {secondColumnItems.map((item) => (
                      <S.ListItem key={item.id}>
                        {item.showBullet !== false && <S.Bullet>•</S.Bullet>}
                        <Text as="span" variant="bodyMd">
                          {item.label}
                        </Text>
                      </S.ListItem>
                    ))}
                  </BlockStack>
                )}
              </>
            )}
        </S.Content>

        <S.CloseButton onClick={handleClose}>
          <XIcon />
        </S.CloseButton>
      </S.BannerContent>
    </S.BannerContainer>
  );
};

const CustomIcon = memo(() => (
  <svg xmlns="http://www.w3.org/2000/svg" width={60} height={60} viewBox="0 0 60 60" fill="none">
    <g clipPath="url(#clip0_3147_11005)">
      <path
        d="M12.4219 15.9375C10.4512 15.9375 8.90625 17.4825 8.90625 19.4531C8.90625 21.4237 10.4512 22.9688 12.4219 22.9688C14.3925 22.9688 15.9375 21.4237 15.9375 19.4531C15.9375 17.4825 14.3925 15.9375 12.4219 15.9375ZM22.9688 19.4531C22.9688 17.4825 21.4237 15.9375 19.4531 15.9375C17.4825 15.9375 15.9375 17.4825 15.9375 19.4531C15.9375 21.4237 17.4825 22.9688 19.4531 22.9688C21.4237 22.9688 22.9688 21.4237 22.9688 19.4531ZM26.4844 22.9688C28.455 22.9688 30 21.4237 30 19.4531C30 17.4825 28.455 15.9375 26.4844 15.9375C24.5138 15.9375 22.9688 17.4825 22.9688 19.4531C22.9688 21.4237 24.5138 22.9688 26.4844 22.9688ZM59.0284 56.6698L59.025 56.668C56.8243 55.3153 56.093 52.3468 57.4113 50.1427C59.128 47.1558 60 43.9269 60 40.5469C60 29.885 51.2071 21.2109 40.5469 21.2109C38.9848 21.2109 37.505 21.3826 36.1489 21.7191C35.5104 21.8787 35.0126 22.3852 34.8615 23.0271C33.5088 28.8601 28.8636 33.5053 23.0272 34.8632C22.7121 34.9366 22.4233 35.0955 22.1926 35.3223C21.9618 35.5491 21.798 35.8351 21.7192 36.1489C21.3826 37.5016 21.2109 38.9814 21.2109 40.5469C21.2109 51.2087 29.8867 60 40.5469 60H58.2422C59.0558 60 59.7666 59.4387 59.952 58.6455C60.1407 57.8525 59.7563 57.0337 59.0284 56.6698Z"
        fill="url(#paint0_linear_3147_11005)"
      />
      <path
        d="M19.4531 0C8.80066 0 0 8.80066 0 19.4531C0 22.828 0.878906 26.1327 2.56629 28.9804C3.86707 31.2656 3.12891 34.1836 0.878906 35.5195C0.175781 35.9062 -0.140508 36.7147 0.0704297 37.4883C0.281367 38.2616 0.949336 38.7891 1.75781 38.7891H19.4531C21 38.7891 22.5117 38.6133 23.8125 38.2969C30.9493 36.6445 36.6445 30.9491 38.2969 23.8477C38.6134 22.5117 38.7891 21.0352 38.7891 19.4531C38.7891 8.80066 30.1056 0 19.4531 0ZM12.4219 21.2109C11.4374 21.2109 10.6641 20.4374 10.6641 19.4531C10.6641 18.4686 11.4374 17.6953 12.4219 17.6953C13.4064 17.6953 14.1797 18.4686 14.1797 19.4531C14.1797 20.4374 13.4064 21.2109 12.4219 21.2109ZM19.4531 21.2109C18.4686 21.2109 17.6953 20.4374 17.6953 19.4531C17.6953 18.4686 18.4686 17.6953 19.4531 17.6953C20.4376 17.6953 21.2109 18.4686 21.2109 19.4531C21.2109 20.4374 20.4376 21.2109 19.4531 21.2109ZM26.4844 21.2109C25.4999 21.2109 24.7266 20.4374 24.7266 19.4531C24.7266 18.4686 25.4999 17.6953 26.4844 17.6953C27.4689 17.6953 28.2422 18.4686 28.2422 19.4531C28.2422 20.4374 27.4689 21.2109 26.4844 21.2109ZM33.5156 38.7891C32.5448 38.7891 31.7578 39.5761 31.7578 40.5469C31.7578 41.5177 32.5448 42.3047 33.5156 42.3047C34.4864 42.3047 35.2734 41.5177 35.2734 40.5469C35.2734 39.5761 34.4864 38.7891 33.5156 38.7891ZM40.5469 38.7891C39.5761 38.7891 38.7891 39.5761 38.7891 40.5469C38.7891 41.5177 39.5761 42.3047 40.5469 42.3047C41.5177 42.3047 42.3047 41.5177 42.3047 40.5469C42.3047 39.5761 41.5177 38.7891 40.5469 38.7891ZM47.5781 38.7891C46.6073 38.7891 45.8203 39.5761 45.8203 40.5469C45.8203 41.5177 46.6073 42.3047 47.5781 42.3047C48.5489 42.3047 49.3359 41.5177 49.3359 40.5469C49.3359 39.5761 48.5489 38.7891 47.5781 38.7891Z"
        fill="url(#paint1_linear_3147_11005)"
      />
    </g>
    <defs>
      <linearGradient id="paint0_linear_3147_11005" x1="34.4531" y1={60} x2="34.4531" y2="15.9375" gradientUnits="userSpaceOnUse">
        <stop stopColor="#3A89FF" />
        <stop offset={1} stopColor="#81CFFF" />
      </linearGradient>
      <linearGradient id="paint1_linear_3147_11005" x1="24.668" y1="42.3047" x2="24.668" y2={0} gradientUnits="userSpaceOnUse">
        <stop stopColor="#BEE3FF" />
        <stop offset={1} stopColor="#F1F5FF" />
      </linearGradient>
      <clipPath id="clip0_3147_11005">
        <rect width={60} height={60} fill="white" />
      </clipPath>
    </defs>
  </svg>
));

export default HelpBanner;
